import PropTypes from 'prop-types';
import styles from './errorList.module.scss';

/**
 * ErrorList — compact, dismissible inline alert for step-level
 * errors (API failures, cross-field validation). Designed to be
 * placed near the action that caused the error (e.g. above the
 * Next button), not as a page-top block.
 *
 * Visual language matches the project design tokens:
 *   • Accent border on the left ($error / $success)
 *   • Soft tinted background, not a hard red block
 *   • Warning/check icon aligned with the message
 *   • Optional dismiss button (X) when onDismiss is provided
 *
 * Accepts either a single string or an object of field → message.
 * If multiple messages, they render as a compact bullet list.
 */
const WARNING_ICON = (
  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M10 2L2 17h16L10 2z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      fill="currentColor"
      fillOpacity="0.1"
    />
    <path d="M10 8v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="10" cy="14.5" r="0.9" fill="currentColor" />
  </svg>
);

const SUCCESS_ICON = (
  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle
      cx="10"
      cy="10"
      r="8"
      stroke="currentColor"
      strokeWidth="1.6"
      fill="currentColor"
      fillOpacity="0.1"
    />
    <path
      d="M6 10l3 3 5-5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CLOSE_ICON = (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ErrorList = ({ title, errors, variant = 'error', onDismiss }) => {
  const messages = Array.isArray(errors)
    ? errors.filter(Boolean)
    : Object.values(errors || {}).filter(Boolean);

  if (!messages.length) return null;

  const isSuccess = variant === 'success';
  const icon = isSuccess ? SUCCESS_ICON : WARNING_ICON;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`${styles.alert} ${isSuccess ? styles.success : styles.error}`}
    >
      <span className={styles.icon}>{icon}</span>

      <div className={styles.body}>
        {title && <p className={styles.title}>{title}</p>}
        {messages.length === 1 ? (
          <p className={styles.message}>{messages[0]}</p>
        ) : (
          <ul className={styles.list}>
            {messages.map((msg, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{msg}</li>
            ))}
          </ul>
        )}
      </div>

      {onDismiss && (
        <button
          type="button"
          className={styles.close}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onDismiss();
          }}
          aria-label="Dismiss"
        >
          {CLOSE_ICON}
        </button>
      )}
    </div>
  );
};

ErrorList.propTypes = {
  title: PropTypes.string,
  errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  variant: PropTypes.oneOf(['error', 'success']),
  onDismiss: PropTypes.func,
};

export default ErrorList;
